import { createStyles, Grid, makeStyles, Typography } from '@material-ui/core';
import sicrediColor from 'assets/styles/default.scss';
import { AxiosError } from 'axios';
import { CircleIcCheck, SyncIcon } from 'components/Icons';
import LayoutContext from 'components/Layout/LayoutContext';
import translation from 'locales/translation';
import {
  Button,
  ButtonThemes,
  Select,
  ToastTypes,
} from 'plataforma-fundacao-componentes';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  assessorActionListarRepresentantes,
  assessorActionSubstituirRepresentantes,
} from 'store/modules/assessor/actions';
import { modalsActionsClose } from 'store/modules/ModalsActions';
import { IReducer } from 'store/Reducer';
import { IModalProps } from '../../../../store/modules/ModalsReducer';
import GenericModal, { IGenericModalProps } from '../GenericModal/GenericModal';

export interface IAlterarRepresentanteModalGenericProps
  extends IGenericModalProps {
  key?: string;
  idEvento: number;
  agencia: string;
  nucleo: string;
  flgModificado?: boolean;
  onSend?: any;
}

export interface IAlterarRepresentanteModal extends IModalProps {
  genericProps?: IAlterarRepresentanteModalGenericProps;
}

const useStyles = makeStyles(() =>
  createStyles({
    actionsContent: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    somenteUmaAlteracao: {
      marginBottom: '1rem',
      fontSize: '12px',
      fontStyle: 'italic',
      color: sicrediColor.secondarySicrediDarker,
    },
  })
);

export default function AlterarRepresentanteModal({
  genericProps,
  open,
}: IAlterarRepresentanteModal) {
  const classes = useStyles();
  const [salvando, setSalvando] = useState<boolean>(false);
  const dispatchAction = useDispatch();
  const currentEvent = useSelector(
    (state: IReducer) => state.assembleiasAssessor.evento
  );

  const [value, setValue] = useState<number | any>();
  const [representante, setRepresentante] = useState<any>();
  const [options, setOptions] = useState<any>([]);
  const { showToast } = useContext(LayoutContext);

  const toClose = useCallback((): void => {
    if (genericProps && genericProps.key) {
      dispatchAction(modalsActionsClose(genericProps.key));
    }
  }, [dispatchAction, genericProps]);

  const toSend = useCallback((): void => {
    setSalvando(false);
    toClose();
    setTimeout(() => {
      setValue(
        representante!
          ?.filter((x: any) => x.ativo === true)
          .map((x: any) => x.idConvocado)[0]
      );
    }, 200);
  }, [representante, toClose]);

  const showError = (error: any) => {
    setSalvando(false);
    showToast({
      pauseOnFocusLoss: true,
      theme: ToastTypes.Error,
      showStatusBar: true,
      timeout: 4000,
      label:
        translation.Toasts.Errors.Keys[
          `${
            (error as AxiosError<Error>).response?.data.message ||
            (error as AxiosError<Error>).response?.data ||
            (error as AxiosError<Error>).message
          }` as keyof typeof translation.Toasts.Errors.Keys
        ] ?? translation.Toasts.Errors.defaultAlternative,
    });
  };

  const loadRepresentantes = useCallback(() => {
    dispatchAction(
      assessorActionListarRepresentantes(
        genericProps!?.agencia,
        currentEvent!?.id,
        genericProps!?.nucleo,
        (resp) => {
          setRepresentante(resp);
        }
      )
    );
  }, [currentEvent, dispatchAction, genericProps]);

  const confirmar = () => {
    setSalvando(true);
    dispatchAction(
      assessorActionSubstituirRepresentantes(
        genericProps!?.agencia,
        value,
        currentEvent!?.id,
        genericProps!?.nucleo,
        toSend,
        genericProps?.onSend(),
        showError
      )
    );
  };

  useEffect(() => {
    loadRepresentantes();
    setValue(
      representante!
        ?.filter((x: any) => x.ativo === true)
        .map((x: any) => x.idConvocado)[0]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRepresentantes]);

  const validation = useMemo(() => {
    const filter: any = representante!
      ?.filter((x: any) => x.ativo === true)
      .map((x: any) => x.idConvocado)[0];

    return filter === value;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (Array.isArray(representante) && representante.length) {
      setOptions(
        representante?.map((rep: any) => {
          return {
            label: `${rep.nomeAssociado} (${rep.nomeCargo})`,
            value: rep.idConvocado,
          };
        })
      );
      setValue(
        representante!
          ?.filter((x: any) => x.ativo === true)
          .map((x: any) => x.idConvocado)[0]
      );
    }
  }, [representante]);

  return (
    <GenericModal
      open={open ?? false}
      title={translation.AlterarRepresentanteModal.title}
      content={
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid item xs={12}>
              <Typography
                component='h5'
                className={classes.somenteUmaAlteracao}
              >
                {translation.AlterarRepresentanteModal.somenteUmaAlteracao}
              </Typography>
              <Typography component='h1'>
                {translation.AlterarRepresentanteModal.subTitle}
              </Typography>
            </Grid>
            <Grid>
              <Select
                placeholder='Selecione um representante'
                options={options}
                onChange={(evt) => {
                  setValue(evt);
                }}
                clearButton={false}
                value={value}
                disabled={options!?.length <= 1 || genericProps?.flgModificado}
                preSelectUniqueOption
              />
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.actionsContent}>
            <Button
              theme={ButtonThemes.Primary}
              onClick={confirmar}
              disabled={
                options!?.length <= 1 ||
                validation ||
                genericProps?.flgModificado
              }
              leftIcon={
                salvando ? (
                  <SyncIcon className='animation-rotate' />
                ) : (
                  <CircleIcCheck />
                )
              }
            >
              {translation.AlterarRepresentanteModal.confirmarButton}
            </Button>
          </Grid>
        </Grid>
      }
      genericProps={{
        maxWidth: 'sm',
        onClose: !salvando ? toClose : undefined,
      }}
    />
  );
}
